import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@shadowhealth/oidc-login";
import { enqueueSnackbar } from "notistack";
import callApi from "../helpers/callApi";
import { Alert, Box, Card, CardContent, LinearProgress, Stack, Typography } from "@mui/material";
import ComparisonTable, { SORT_FIELD } from "../components/ComparisonTable";
import ComparisonToolbar from "../components/ComparisonToolbar";
import ExportCsvButton from "../components/ExportCsvButton";
import RunLabels from "../components/RunLabels";
import TurnLevelSummary from "../components/TurnLevelSummary";
import { comparisonRoute, mapComparisonPage } from "../helpers/evalComparisonApi";
import useComparisonViewState from "../hooks/useComparisonViewState";

// Format preset key into readable label: "convoengine_plus" → "ConvoEngine Plus"
const formatPreset = (preset) => {
  if (!preset) return preset;
  const overrides = {
    convoengine_plus: "ConvoEngine Plus",
    cws: "CWS",
    semantic_only: "Semantic Only",
    lexical_vector_only: "Lexical Vector Only",
  };
  return overrides[preset.toLowerCase()] || preset.replaceAll("_", " ").replaceAll(/\b\w/g, (c) => c.toUpperCase());
};

const RunStatusBanner = ({ runId, token, onDone }) => {
  const [runStatus, setRunStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!runId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const resp = await callApi(`/eval/runs/${runId}`, "GET", undefined, token);
        if (cancelled) return;
        setRunStatus(resp.status);
        if (resp.status === "queued" || resp.status === "running") {
          setTimeout(poll, 3000);
        } else if (resp.status === "done") {
          onDone?.();
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [runId, token, onDone]);

  if (!runId || runStatus === "done") return null;
  if (error) return <Alert severity="error">Could not fetch run status: {error}</Alert>;
  if (!runStatus) return null;
  if (runStatus === "failed")
    return <Alert severity="error">Eval run failed. Check the background jobs page for details.</Alert>;

  return (
    <Alert severity="info" icon={false}>
      <Stack spacing={1}>
        <Typography variant="body2">
          Eval run <strong>{runStatus}</strong> — results will appear automatically when done
        </Typography>
        <LinearProgress />
      </Stack>
    </Alert>
  );
};

const DEFAULT_PAGE_SIZE = 50;
const EMPTY_PAGE = { rows: [], total: 0, counts: null, pinned: null };

// The latest batch for each engine version, in the order the report lists them.
const latestBatches = (batches) => {
  const byPreset = new Map();
  batches.forEach((b) => {
    const seen = byPreset.get(b.preset);
    if (!seen || new Date(b.created_at) > new Date(seen.created_at)) byPreset.set(b.preset, b);
  });
  return [...byPreset.values()];
};

const EvaluationDetailPage = () => {
  const { tag } = useParams();
  const auth = useAuth();
  const token = auth.user?.access_token;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const runId = new URLSearchParams(globalThis.location.search).get("run_id");

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await callApi(`/eval/tags/${encodeURIComponent(tag)}/report`, "GET", undefined, token);
      setReport(resp);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [tag, token]);

  useEffect(() => {
    if (!runId) fetchReport();
  }, [fetchReport]); // noqa

  const batches = useMemo(() => latestBatches(report?.batches || []), [report]);
  const presets = useMemo(() => batches.map((b) => b.preset), [batches]);
  const scripts = useMemo(() => report?.scripts || [], [report]);
  const versions = useMemo(() => presets.map((key) => ({ key, label: formatPreset(key) })), [presets]);
  const roles = useMemo(() => ({ production: report?.production_preset, test: report?.test_preset }), [report]);
  const { view, update } = useComparisonViewState({ presets, scripts, roles });
  // Columns go production, then test (view.a, view.b), each named with its role.
  const compared = [view.a, view.b]
    .filter(Boolean)
    .map((key) => ({ key, label: `${key === view.a ? "Production" : "Test"}: ${formatPreset(key)}` }));

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [comparison, setComparison] = useState(EMPTY_PAGE);
  const [rowsLoading, setRowsLoading] = useState(false);
  // The batch ids the first diff page returned, kept with the engine version pair they
  // belong to, so later pages stay on the same batches and a new pair starts fresh.
  const [pinned, setPinned] = useState(null);
  const pairKey = `${view.a}|${view.b}`;
  const pinnedIds = pinned?.pair === pairKey ? pinned.ids : null;

  // A filter change starts again at page 1.
  const changeView = (patch) => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
    update(patch);
  };

  const batchId = batches.find((b) => b.preset === view.a)?.id;
  useEffect(() => {
    if (!view.a) return undefined;
    let cancelled = false;
    const { page, pageSize } = paginationModel;
    setRowsLoading(true);
    callApi(comparisonRoute({ tag, view, batchId, pinned: pinnedIds, page, pageSize }), "GET", undefined, token)
      .then((resp) => {
        if (cancelled) return;
        const mapped = mapComparisonPage(resp, view, page * pageSize);
        setComparison(mapped);
        if (!pinnedIds && mapped.pinned) setPinned({ pair: pairKey, ids: mapped.pinned });
      })
      .catch((err) => {
        if (cancelled) return;
        // Clear the old rows so the table never shows them under the new filters.
        setComparison(EMPTY_PAGE);
        enqueueSnackbar(err.message, { variant: "error" });
      })
      .finally(() => {
        if (!cancelled) setRowsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `pinnedIds` comes from the first page of this pair; later pages reuse it without a refetch loop.
  }, [tag, view, batchId, paginationModel, token]);

  const sortModel = view.sort
    ? [{ field: Object.keys(SORT_FIELD).find((f) => SORT_FIELD[f] === view.sort), sort: view.order }]
    : [];
  const handleSortModelChange = (model) => {
    const [first] = model;
    changeView(first ? { sort: SORT_FIELD[first.field], order: first.sort } : { sort: null, order: "asc" });
  };

  const first = batches[0];
  const batchFor = (key) => batches.find((b) => b.preset === key) || {};

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5" component="h1">
              Evaluation:{" "}
              <Box component="span" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                {decodeURIComponent(tag)}
              </Box>
            </Typography>
            {first && (
              <RunLabels
                goldenSetName={report.golden_set_name || first.csv_file}
                goldenSetVersion={first.golden_set_version}
                scripts={scripts}
                production={view.a && formatPreset(view.a)}
                test={view.b && formatPreset(view.b)}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {runId && <RunStatusBanner runId={runId} token={token} onDone={fetchReport} />}

      {loading && (
        <Box sx={{ px: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {!loading && report && batches.length === 0 && (
        <Alert severity="info">No results yet for this run. They will appear when the run completes.</Alert>
      )}

      {batches.length > 0 && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box data-testid="summary-area">
                <TurnLevelSummary
                  goldenSet={report.golden_set_expected}
                  production={view.a && { label: formatPreset(view.a), batch: batchFor(view.a) }}
                  test={view.b && { label: formatPreset(view.b), batch: batchFor(view.b) }}
                />
              </Box>
              <ComparisonToolbar
                versions={versions}
                view={view}
                scripts={scripts}
                counts={comparison.counts}
                onChange={changeView}
                actions={
                  <ExportCsvButton
                    tag={tag}
                    view={view}
                    batchId={batchId}
                    pinned={pinnedIds}
                    token={token}
                    loading={rowsLoading}
                    total={comparison.total}
                  />
                }
              />
              <ComparisonTable
                rows={comparison.rows}
                versions={compared}
                total={comparison.total}
                loading={rowsLoading}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
              />
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default EvaluationDetailPage;
